import { useState } from "react";
import { ChevronDown, Pencil, Sparkles, Loader2 } from "lucide-react";
import { US_STATES } from "@/lib/states";

const field =
  "w-full rounded-xl border border-sbg-raspberry/20 bg-white px-3.5 py-2.5 text-sm text-sbg-burgundy outline-none transition-colors placeholder:text-sbg-burgundy/40 focus:border-sbg-fuchsia";
const lbl = "mb-1.5 block text-sm font-bold text-sbg-burgundy";

export default function CaptionCustomizer({ form, setForm, onGenerate, generating }) {
  const [open, setOpen] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const hasDetails = !!(form.project_title || form.project_description || form.project_serves || form.project_when || form.city);

  return (
    <div className="mt-10 overflow-hidden rounded-3xl bg-white ring-1 ring-sbg-raspberry/10" data-testid="caption-customizer">
      <button
        type="button"
        data-testid="caption-customizer-toggle"
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center justify-between gap-3 p-6 text-left sm:p-8"
      >
        <span className="flex items-start gap-3">
          <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-2xl bg-sbg-blush text-sbg-raspberry">
            <Pencil className="h-4 w-4" />
          </span>
          <span>
            <span className="block font-display text-xl font-bold text-sbg-burgundy sm:text-2xl">Customize Your Captions</span>
            <span className="mt-1 block text-sm text-sbg-burgundy/70">
              Add your project details for captions written just for you. These carry over to your page.
            </span>
          </span>
        </span>
        <ChevronDown
          className={`h-5 w-5 shrink-0 text-sbg-raspberry transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>

      {open && (
        <div className="animate-fade-up space-y-5 border-t border-sbg-raspberry/10 p-6 sm:p-8" data-testid="caption-customizer-body">
          <div>
            <label className={lbl}>Project Name</label>
            <input data-testid="cc-project-title" value={form.project_title} onChange={set("project_title")} placeholder="My Joy Project" className={field} />
          </div>
          <div>
            <label className={lbl}>What Does Your Project Do?</label>
            <textarea data-testid="cc-description" value={form.project_description} onChange={set("project_description")} rows={3} placeholder="Finish the sentence: My project..." className={field} />
          </div>
          <div className="grid gap-5 sm:grid-cols-2">
            <div>
              <label className={lbl}>Who Will It Serve?</label>
              <input data-testid="cc-serves" value={form.project_serves} onChange={set("project_serves")} placeholder="Girls in my city" className={field} />
            </div>
            <div>
              <label className={lbl}>When Will It Happen?</label>
              <input data-testid="cc-when" value={form.project_when} onChange={set("project_when")} placeholder="This fall" className={field} />
            </div>
          </div>
          <div className="grid gap-5 sm:grid-cols-2">
            <div>
              <label className={lbl}>City Or Community</label>
              <input data-testid="cc-city" value={form.city} onChange={set("city")} placeholder="Selma" className={field} />
            </div>
            <div>
              <label className={lbl}>State</label>
              <select data-testid="cc-state" value={form.state} onChange={set("state")} className={field}>
                <option value="">Choose a state</option>
                {US_STATES.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="flex flex-col items-stretch gap-2 border-t border-sbg-raspberry/10 pt-5 sm:flex-row sm:items-center sm:justify-end">
            {!hasDetails && (
              <span className="text-xs font-medium text-sbg-burgundy/60">Add at least one detail above to unlock.</span>
            )}
            <button
              type="button"
              data-testid="customize-my-caption-btn"
              onClick={onGenerate}
              disabled={generating || !hasDetails}
              title={hasDetails ? "" : "Add a project detail above first"}
              className="inline-flex items-center justify-center gap-2 rounded-full bg-sbg-fuchsia px-6 py-3 text-sm font-bold text-white transition-transform hover:-translate-y-0.5 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {generating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
              {generating ? "Working…" : "Customize My Captions"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
